/* ============================================================
   hero-fumo.js — fundo de fumo animado em WebGL
   ============================================================

   O QUE FAZ:
   Pinta uma <canvas> com um campo de fumo escuro que se move em
   contínuo, nas cores da marca. Substitui a fotografia de fundo do
   hero.

   DE ONDE VEM:
   Adaptado do "Mesh drift" do Shader Builder da 21st.dev, que vinha
   em React/TypeScript. Este tema é Liquid puro, sem build e sem
   React, por isso foi reescrito em JS simples. Mantive dele o que
   era mesmo bom — o teto de pixels, o hash de grão do Dave Hoskins,
   e a suspensão por IntersectionObserver/visibilitychange — e troquei
   o campo de manchas radiais por um domain warp encadeado, que é o
   que dá o aspecto de fumo em vez de gradiente de malha.

   COMO SE USA:
   <canvas data-hero-fumo
           data-cores="#05070a,#0a0c0e,#0b5fd8,#5c9bff"
           data-velocidade="0.16"
           data-grao="0.085"></canvas>

   PORQUE NAO HA INTERACAO COM O RATO:
   o original trazia distorcao sob o cursor. Num hero que tem dois
   botoes, mexer o fundo enquanto a pessoa aponta para "Ver produtos"
   distrai do gesto que interessa — e obrigava a um listener de
   pointermove global em todas as paginas.

   ACESSIBILIDADE:
   com "prefers-reduced-motion" desenha um fotograma e para. O fundo
   fica igual, apenas nao se mexe.
   ============================================================ */

(function () {
  'use strict';

  var VERT =
    'attribute vec2 a_position;' +
    'void main(){ gl_Position = vec4(a_position, 0.0, 1.0); }';

  var FRAG = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    '',
    'uniform vec3 u_cores[5];',
    'uniform vec4 u_cena;    // resolucao.xy, tempo, n de cores',
    'uniform vec4 u_ajuste;  // escala, contraste, vinheta, grao',
    '',
    '#define u_res u_cena.xy',
    '#define u_t u_cena.z',
    '#define u_n u_cena.w',
    '#define u_escala u_ajuste.x',
    '#define u_contraste u_ajuste.y',
    '#define u_vinheta u_ajuste.z',
    '#define u_grao u_ajuste.w',
    '',
    'float hash21(vec2 p){',
    '#ifndef GL_FRAGMENT_PRECISION_HIGH',
    '  p = mod(p, 31.0);',
    '#endif',
    '  p = fract(p * vec2(234.34, 435.345));',
    '  p += dot(p, p + 34.23);',
    '  return fract(p.x * p.y);',
    '}',
    '',
    // Ruido branco uniforme para o grao (hash12 do Dave Hoskins). O hash
    // multiplicativo acima serve para o ruido de valor, mas deixa uma malha
    // alinhada aos eixos nas zonas lisas, que se le como uma rede.
    'float graoHash(vec2 p){',
    '  vec3 p3 = fract(vec3(p.xyx) * 0.1031);',
    '  p3 += dot(p3, p3.yzx + 33.33);',
    '  return fract((p3.x + p3.y) * p3.z);',
    '}',
    '',
    'float ruido(vec2 p){',
    '  vec2 i = floor(p); vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash21(i), hash21(i + vec2(1.0,0.0)), u.x),',
    '             mix(hash21(i + vec2(0.0,1.0)), hash21(i + vec2(1.0,1.0)), u.x), u.y);',
    '}',
    '',
    'float fbm(vec2 p){',
    '  float v = 0.0; float a = 0.5;',
    '  for (int i = 0; i < 5; i++){',
    '    v += a * ruido(p);',
    '    p = p * 2.03 + vec2(17.0, 9.2);',
    '    a *= 0.5;',
    '  }',
    '  return v;',
    '}',
    '',
    // Mistura em espaco linear. Em sRGB, a passagem do carbono para o azul
    // passava por um cinzento lavado a meio do degrade.
    'vec3 paraLinear(vec3 c){ return c * c; }',
    'vec3 paraSrgb(vec3 c){ return sqrt(max(c, 0.0)); }',
    '',
    'vec3 paleta(float x){',
    '  float n = max(u_n - 1.0, 1.0);',
    '  float f = clamp(x, 0.0, 1.0) * n;',
    '  vec3 c = paraLinear(u_cores[0]);',
    '  for (int i = 0; i < 4; i++){',
    '    if (float(i) < n)',
    '      c = mix(c, paraLinear(u_cores[i + 1]),',
    '              smoothstep(0.0, 1.0, clamp(f - float(i), 0.0, 1.0)));',
    '  }',
    '  return paraSrgb(c);',
    '}',
    '',
    'void main(){',
    '  vec2 ecra = gl_FragCoord.xy / u_res;',
    '  vec2 p = (gl_FragCoord.xy - 0.5 * u_res) / min(u_res.x, u_res.y);',
    '  p *= u_escala;',
    '  float t = u_t;',
    '',
    // Domain warp encadeado (o metodo do Inigo Quilez). Cada camada arrasta a
    // seguinte, e cada uma deriva no tempo a ritmo e direccao diferentes: e
    // dai que vem o enrolar do fumo. Uma so camada dava nuvens que pulsavam
    // no sitio em vez de rodopiarem.
    '  vec2 q = vec2(fbm(p + vec2(0.0, 0.0) + t * 0.060),',
    '                fbm(p + vec2(5.2, 1.3) - t * 0.048));',
    '  vec2 r = vec2(fbm(p + 3.4 * q + vec2(1.7, 9.2) + t * 0.085),',
    '                fbm(p + 3.4 * q + vec2(8.3, 2.8) - t * 0.071));',
    '  float d = fbm(p + 3.6 * r);',
    '',
    // As zonas onde as duas camadas concordam ficam mais densas: sao os
    // rolos claros do fumo. Sem isto o campo era uniforme demais.
    '  float densidade = clamp(d * 0.78 + length(r) * 0.30, 0.0, 1.0);',
    '  densidade = pow(densidade, u_contraste);',
    '',
    '  vec3 col = paleta(densidade);',
    '',
    '  if (u_vinheta > 0.0001){',
    '    float vd = length(ecra - 0.5) * 1.41421356;',
    '    col *= 1.0 - u_vinheta * smoothstep(0.30, 1.0, vd);',
    '  }',
    '',
    // O grao entra por ultimo e e o que da o aspecto fosco. Tambem serve de
    // dither: sem ele, um degrade tao escuro mostra bandas em ecras de 8 bits.
    '  if (u_grao > 0.0001)',
    '    col += (graoHash(gl_FragCoord.xy + vec2(t * 11.0, t * 7.0)) - 0.5) * u_grao;',
    '',
    '  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);',
    '}'
  ].join('\n');

  function hexParaRgb(hex) {
    var h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n) || h.length !== 6) return [0, 0, 0];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function iniciar(canvas) {
    if (canvas.dataset.fumoLigado === 'sim') return;

    var gl = canvas.getContext('webgl', { antialias: false, depth: false, alpha: false })
      || canvas.getContext('experimental-webgl', { antialias: false, depth: false, alpha: false });

    // Sem WebGL nao ha erro nem canvas vazia: fica o degrade CSS que o
    // hero ja pinta por baixo, que e da mesma familia de cores.
    if (!gl) return;

    canvas.dataset.fumoLigado = 'sim';

    function compilar(tipo, src) {
      var s = gl.createShader(tipo);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    }

    var prog = gl.createProgram();
    var vs = compilar(gl.VERTEX_SHADER, VERT);
    var fs = compilar(gl.FRAGMENT_SHADER, FRAG);
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      gl.deleteProgram(prog);
      delete canvas.dataset.fumoLigado;
      return;
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    gl.useProgram(prog);

    // Um triangulo que cobre o ecra inteiro — mais barato do que dois
    // que formam um quadrado, porque nao ha diagonal a ser rasterizada duas vezes.
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uCores = gl.getUniformLocation(prog, 'u_cores');
    var uCena = gl.getUniformLocation(prog, 'u_cena');
    var uAjuste = gl.getUniformLocation(prog, 'u_ajuste');

    var lista = (canvas.dataset.cores || '#05070a,#0a0c0e,#0b5fd8,#5c9bff')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean).slice(0, 5);
    var nCores = lista.length;
    var planas = [];
    for (var i = 0; i < 5; i++) {
      var c = hexParaRgb(lista[Math.min(i, nCores - 1)]);
      planas.push(c[0], c[1], c[2]);
    }
    gl.uniform3fv(uCores, new Float32Array(planas));

    var num = function (chave, omissao) {
      var v = parseFloat(canvas.dataset[chave]);
      return isFinite(v) ? v : omissao;
    };
    var escala = num('escala', 2.1);
    var contraste = num('contraste', 1.45);
    var vinheta = num('vinheta', 0.22);
    var grao = num('grao', 0.085);
    var velocidade = num('velocidade', 0.16);
    gl.uniform4f(uAjuste, escala, contraste, vinheta, grao);

    var raf = 0, inicio = performance.now(), visivel = true, aVista = true, morto = false;
    var reduzido = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

    function dimensionar() {
      var r = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var bw = Math.max(1, Math.round(r.width * dpr));
      var bh = Math.max(1, Math.round(r.height * dpr));
      // Teto de 2 megapixels: num ecra 4K a retina, o hero pedia mais de 8 MP
      // por fotograma e o portatil punha as ventoinhas a trabalhar por um fundo.
      var k = Math.min(1, Math.sqrt(2000000 / Math.max(1, bw * bh)));
      var w = Math.max(1, Math.round(bw * k));
      var h = Math.max(1, Math.round(bh * k));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    function desenhar(agora) {
      raf = 0;
      if (morto) return;
      dimensionar();
      var t = ((agora - inicio) / 1000) * velocidade;
      gl.uniform4f(uCena, canvas.width, canvas.height, t, nCores);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (visivel && aVista && !(reduzido && reduzido.matches)) pedir();
    }

    function pedir() {
      if (!morto && raf === 0) raf = requestAnimationFrame(desenhar);
    }

    function parar() {
      if (raf !== 0) { cancelAnimationFrame(raf); raf = 0; }
    }

    var ro = new ResizeObserver(function () { dimensionar(); pedir(); });
    ro.observe(canvas);

    // Fora do ecra nao se desenha. O hero sai de vista mal se rola a
    // pagina, e sem isto o portatil continuava a render a 60 fps um fundo
    // que ninguem estava a ver.
    var io = new IntersectionObserver(function (e) {
      aVista = e[0] ? e[0].isIntersecting : true;
      if (aVista) pedir(); else parar();
    });
    io.observe(canvas);

    function aoMudarVisibilidade() {
      visivel = document.visibilityState === 'visible';
      if (visivel) pedir(); else parar();
    }
    document.addEventListener('visibilitychange', aoMudarVisibilidade);

    if (reduzido && reduzido.addEventListener)
      reduzido.addEventListener('change', function () { pedir(); });

    pedir();

    // Suspender, nao destruir. Uma versao anterior marcava "morto" no
    // pagehide: quem carregasse em "voltar" no navegador apanhava a pagina
    // restaurada da bfcache com o fumo parado para sempre, porque o
    // pagehide nao volta a disparar ao regressar.
    window.addEventListener('pagehide', parar);
    window.addEventListener('pageshow', function () { pedir(); });
  }

  /* ----------------------------------------------------------
     Cabecalho sobreposto ao fumo
     ----------------------------------------------------------
     Duas coisas:

     1. Mede a altura real do cabecalho (faixa-topo + nav) e escreve-a
        em --altura-cabecalho. O hero usa-a para levantar a canvas por
        tras da barra. Medir e preciso porque a faixa-topo pode ser
        desligada, ter duas linhas em ecra estreito, ou o lojista pode
        mudar a altura do logotipo.

     2. Poe .esta-preso no cabecalho assim que a pagina rola. Sem isto,
        um cabecalho transparente sobre a grelha de produtos branca
        deixava o menu ilegivel.
     ---------------------------------------------------------- */
  function cabecalhoSobreposto() {
    var barra = document.querySelector('[data-cabecalho]');
    if (!barra) return;
    var topo = document.querySelector('.faixa-topo');

    function medir() {
      var h = barra.offsetHeight + (topo ? topo.offsetHeight : 0);
      document.documentElement.style.setProperty('--altura-cabecalho', h + 'px');
    }

    // O limiar e a altura da faixa-topo: a barra passa a solida quando
    // deixa de haver hero por tras dela, nao mal se toca na roda.
    function aoRolar() {
      var limiar = topo ? topo.offsetHeight : 8;
      barra.classList.toggle('esta-preso', window.scrollY > limiar);
    }

    medir();
    aoRolar();
    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', function () { medir(); aoRolar(); });
    if (window.ResizeObserver) new ResizeObserver(medir).observe(barra);
  }

  function varrer() {
    var lista = document.querySelectorAll('canvas[data-hero-fumo]');
    for (var i = 0; i < lista.length; i++) iniciar(lista[i]);
    if (lista.length) cabecalhoSobreposto();
  }

  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', varrer);
  else varrer();

  // O editor de temas troca a seccao sem recarregar a pagina.
  document.addEventListener('shopify:section:load', varrer);
})();
